import { createFileRoute, Outlet, useLocation, useNavigate } from '@tanstack/react-router';
import { Ban, Banknote, Building2, Power, UserPlus } from 'lucide-react';
import { useState } from 'react';
import { useSetCommiteeActive } from '@/api/commitee/commitee.mutations';
import { useCommitee } from '@/api/commitee/commitee.queries';
import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmButton } from '@/components/ConfirmButton';
import { StickyHeaderPage } from '@/components/StickyHeaderPage';
import { Spinner } from '@/components/shadcn/Spinner';
import { Tabs, TabsList, TabsTrigger } from '@/components/Tabs';
import { BankDialog } from '@/features/account/bank/BankDialog';
import { WorkplaceDialog } from '@/features/account/workplace/WorkplaceDialog';
import { COMMITEE_STATUS_CONFIG } from '@/utils/commitee.components';
import { formatDate } from '@/utils/date.utils';
import { formatSiret } from '@/utils/string.utils';
import { AddUserDialog } from './$id/components/AddUserDialog';

export const Route = createFileRoute('/gestion/entreprise/$id')({
  component: EntrepriseLayout,
});

function EntrepriseLayout() {
  const { id } = Route.useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { data: commitee, isLoading } = useCommitee(Number(id));
  const [userDialogOpen, setUserDialogOpen] = useState(false);
  const [bankDialogOpen, setBankDialogOpen] = useState(false);
  const [workplaceDialogOpen, setWorkplaceDialogOpen] = useState(false);

  const getCurrentTab = () => {
    const pathname = location.pathname;
    if (pathname.includes('/lieu-de-travail')) return 'lieu-de-travail';
    if (pathname.includes('/banque')) return 'banque';
    return 'users';
  };

  const currentTab = getCurrentTab();

  const handleTabChange = (value: string) => {
    const basePath = `/gestion/entreprise/${id}`;
    if (value === 'lieu-de-travail') {
      navigate({ to: `${basePath}/lieu-de-travail` });
    } else if (value === 'banque') {
      navigate({ to: `${basePath}/banque` });
    } else {
      navigate({ to: `${basePath}/users` });
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Spinner />
      </div>
    );
  }

  if (!commitee) {
    return <p className="text-gray-500">Entreprise introuvable</p>;
  }

  const statusBadge = COMMITEE_STATUS_CONFIG[commitee.status];

  return (
    <StickyHeaderPage
      pathLink={[
        { label: 'Entreprises', navigate: { to: '/gestion/general' } },
        { label: commitee.name },
      ]}
      header={
        <div className="flex items-start justify-between">
          <div className="flex flex-col gap-4">
            <h1 className="text-3xl font-bold text-gray-900">{commitee.name}</h1>
            <div className="grid grid-cols-2 gap-6">
              <div className="flex flex-col gap-1">
                <span className="text-sm text-gray-500">SIRET</span>
                <span className="text-base font-semibold text-gray-900">
                  {formatSiret(commitee.siret)}
                </span>
              </div>
              <div className="flex flex-col gap-1">
                <span className="text-sm text-gray-500">Date création</span>
                <span className="text-base font-semibold text-gray-900">
                  {formatDate(commitee.createdAt)}
                </span>
              </div>
            </div>
          </div>
        </div>
      }
      topRightActions={
        <div className="flex flex-col items-end gap-4">
          <p className="text-sm text-gray-500">Statut de l'entreprise</p>
          <Badge color={statusBadge.color}>{statusBadge.label}</Badge>
          <ToggleActiveButton commiteeId={commitee.id} active={commitee.active} />
        </div>
      }
      subHeader={
        <div className="flex items-center justify-between gap-3">
          <Tabs value={currentTab} onValueChange={handleTabChange}>
            <TabsList>
              <TabsTrigger value="users">Utilisateurs</TabsTrigger>
              <TabsTrigger value="lieu-de-travail">Lieu de travail</TabsTrigger>
              <TabsTrigger value="banque">Banque</TabsTrigger>
            </TabsList>
          </Tabs>
          {currentTab === 'users' && (
            <Button variant="color" icon={UserPlus} onClick={() => setUserDialogOpen(true)}>
              Ajouter un utilisateur
            </Button>
          )}
          {currentTab === 'lieu-de-travail' && (
            <Button variant="color" icon={Building2} onClick={() => setWorkplaceDialogOpen(true)}>
              Ajouter un lieu de travail
            </Button>
          )}
          {currentTab === 'banque' && (
            <Button variant="color" icon={Banknote} onClick={() => setBankDialogOpen(true)}>
              Ajouter une banque
            </Button>
          )}
        </div>
      }
    >
      <Outlet />

      <AddUserDialog
        commiteeId={Number(id)}
        open={userDialogOpen}
        onOpenChange={setUserDialogOpen}
      />
      <BankDialog open={bankDialogOpen} onOpenChange={setBankDialogOpen} />
      <WorkplaceDialog open={workplaceDialogOpen} onOpenChange={setWorkplaceDialogOpen} />
    </StickyHeaderPage>
  );
}

function ToggleActiveButton({ commiteeId, active }: { commiteeId: number; active: boolean }) {
  const setActive = useSetCommiteeActive(commiteeId);

  return (
    <ConfirmButton
      button={(openModal) => (
        <Button
          variant="color"
          color="red"
          icon={active ? Ban : Power}
          onClick={openModal}
          isPending={setActive.isPending}
        >
          {active ? 'Désactiver' : 'Réactiver'}
        </Button>
      )}
      confirmationDescription={
        active
          ? 'Êtes-vous sûr de vouloir désactiver cette entreprise ? Ses utilisateurs ne pourront plus se connecter.'
          : 'Réactiver cette entreprise permettra à ses utilisateurs de se connecter à nouveau.'
      }
      confirmationButtonLabel={active ? 'Désactiver' : 'Réactiver'}
      onConfirm={async () => {
        await setActive.mutateAsync({ active: !active });
      }}
    />
  );
}
